import { FormEvent, useEffect, useState } from 'react';
import type { CategoryRef, StockMove, StockRow, StockWarehouseRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import { DOC_KIND_LABEL, formatDate, formatQty, UNIT_LABEL } from '../format';
import { DataTable, PageHeader, Pager, SearchPanel, useUrlFilters, type Column } from '../components/ListPage';

const PAGE = 25;

// Anbar qalığı (KT-99 şablonu): məhsul üzrə qalıq (fiziki, rezerv, sərbəst), minimum; axtarış paneli (anbar, kateqoriya,
// «minimumdan az»), süzgəc URL-də. Sətrə klik — hərəkət tarixçəsi. Yalnız oxu.
const KEYS = ['q', 'wh', 'cat', 'low', 'page', 'sort'] as const;

export function Stock() {
  const [f, setF] = useUrlFilters(KEYS);
  const [q, setQ] = useState(f.q);
  const [draft, setDraft] = useState({ wh: f.wh, cat: f.cat, low: f.low });
  const [warehouses, setWarehouses] = useState<StockWarehouseRef[]>([]);
  const [categories, setCategories] = useState<CategoryRef[]>([]);
  const [items, setItems] = useState<StockRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<StockRow | null>(null);
  const page = Number(f.page) || 1;

  useEffect(() => {
    api.stockWarehouses().then(setWarehouses).catch(() => setWarehouses([]));
    api.productCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .stock({ q: f.q || undefined, warehouseId: f.wh || undefined, categoryId: f.cat || undefined, low: f.low === '1' ? '1' : undefined, page, limit: PAGE, sort: f.sort || undefined })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Qalıqlar yüklənmədi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [f.q, f.wh, f.cat, f.low, f.sort, page]);

  if (selected) {
    return <StockMoves item={selected} onBack={() => setSelected(null)} />;
  }

  const columns: Column<StockRow>[] = [
    {
      key: 'name',
      label: 'Məhsul',
      sortKey: 'name',
      render: (s) => (
        <button type="button" className="cell-link" onClick={() => setSelected(s)}>
          {s.name}
        </button>
      ),
    },
    { key: 'cat', label: 'Kateqoriya', minor: true, render: (s) => s.category ?? '—' },
    { key: 'unit', label: 'Vahid', minor: true, render: (s) => UNIT_LABEL[s.unit] ?? s.unit },
    { key: 'phys', label: 'Fiziki', align: 'right', mono: true, sortKey: 'qty', render: (s) => <span className={s.low ? 'bad' : undefined}>{formatQty(s.qtyPhys)}</span> },
    { key: 'res', label: 'Rezerv', align: 'right', mono: true, minor: true, render: (s) => formatQty(s.qtyReserved) },
    { key: 'free', label: 'Sərbəst', align: 'right', mono: true, minor: true, render: (s) => formatQty(s.qtyFree) },
    { key: 'transit', label: 'Yolda', align: 'right', mono: true, minor: true, render: (s) => (s.inTransit ? formatQty(s.inTransit) : '—') },
    { key: 'min', label: 'Minimum', align: 'right', mono: true, minor: true, render: (s) => (s.minQty === null ? '—' : formatQty(s.minQty)) },
    { key: 'st', label: 'Vəziyyət', render: (s) => (s.low ? <span className="badge badge-bad">Az qalıb</span> : <span className="badge badge-ok">Normal</span>) },
  ];

  return (
    <main className="page page-list">
      <PageHeader title="Anbar qalığı" />
      <div className="list-bar">
        <form
          className="list-search"
          role="search"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setF({ q: q.trim(), page: '' });
          }}
        >
          <input type="search" aria-label="Axtarış" placeholder="Məhsul adı" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
      </div>

      <SearchPanel
        defaultOpen={Boolean(f.wh || f.cat || f.low)}
        onSearch={() => setF({ ...draft, page: '' })}
        onClear={() => {
          setQ('');
          setDraft({ wh: '', cat: '', low: '' });
          setF({ q: '', wh: '', cat: '', low: '', page: '' });
        }}
      >
        <label className="field">
          Anbar
          <select value={draft.wh} onChange={(e) => setDraft({ ...draft, wh: e.target.value })}>
            <option value="">Bütün anbarlar</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.outletName} · {w.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Kateqoriya
          <select value={draft.cat} onChange={(e) => setDraft({ ...draft, cat: e.target.value })}>
            <option value="">Hamısı</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={draft.low === '1'} onChange={(e) => setDraft({ ...draft, low: e.target.checked ? '1' : '' })} />
          Minimumdan az
        </label>
      </SearchPanel>

      <DataTable
        sort={f.sort || 'name'}
        onSort={(v) => setF({ sort: v === 'name' ? '' : v, page: '' })}
        label="Anbar qalığı"
        columns={columns}
        rows={items}
        rowKey={(s) => s.productId}
        loading={loading}
        error={error}
        empty={<p className="muted">{f.q || f.wh || f.cat || f.low ? 'Bu süzgəclə qalıq tapılmadı.' : 'Anbarda mal yoxdur.'}</p>}
      />
      <Pager page={page} limit={PAGE} total={total} unit="məhsul" onPage={(p) => setF({ page: p > 1 ? String(p) : '' })} />
    </main>
  );
}

interface MovesProps {
  item: StockRow;
  onBack: () => void;
}

// Məhsulun hərəkət tarixçəsi (ən yenilər əvvəl). Anbar və sənəd göstərilir.
function StockMoves({ item, onBack }: MovesProps) {
  const [page, setPage] = useState(1);
  const [moves, setMoves] = useState<StockMove[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .stockMoves(item.productId, { page, limit: PAGE })
      .then((r) => {
        if (cancelled) return;
        setMoves(r.items);
        setTotal(r.total);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Hərəkətlər yüklənmədi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.productId, page]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn" onClick={onBack}>
          ← Anbar
        </button>
        <span className="brand">{item.name}</span>
      </header>

      <main className="page">
        <p className="muted">
          fiziki {formatQty(item.qtyPhys)} · rezerv {formatQty(item.qtyReserved)} · sərbəst {formatQty(item.qtyFree)}
          {item.minQty !== null ? ` · min. ${formatQty(item.minQty)}` : ''}
        </p>

        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {loading && <p className="muted">Yüklənir…</p>}
        {!loading && !error && moves.length === 0 && <p className="muted">Hərəkət yoxdur.</p>}

        {moves.length > 0 && (
          <ul className="product-list">
            {moves.map((m) => (
              <li key={m.id}>
                <div className="product-row">
                  <span className="product-name">
                    {DOC_KIND_LABEL[m.docKind] ?? m.docKind} <span className="mono small">{m.docNumber}</span>
                  </span>
                  <span className="muted small">
                    {formatDate(m.movedAt)} · {m.warehouseName}
                    {m.batchNumber ? ` · partiya ${m.batchNumber}` : ''}
                  </span>
                  <span className="product-meta">
                    <span className={`mono${m.qtyPhys < 0 ? ' bad' : ''}`}>
                      {m.qtyPhys > 0 ? '+' : ''}
                      {formatQty(m.qtyPhys)}
                    </span>
                    {m.qtyReserved !== 0 && <span className="muted small">rezerv {formatQty(m.qtyReserved)}</span>}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {total > PAGE && (
          <nav className="pager" aria-label="Səhifələr">
            <button type="button" className="btn-small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ← Əvvəlki
            </button>
            <span className="muted small">
              {page} / {pages} · cəmi {total}
            </span>
            <button type="button" className="btn-small" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Növbəti →
            </button>
          </nav>
        )}
      </main>
    </div>
  );
}
